/*
 * Pure helpers behind a page's editorial state.
 *
 * The backend stores two fields: `status` ("draft" | "published" | "archived")
 * and `published_at` (ISO string). Most pages predate the status field and have
 * none; the public sites render them regardless, so a missing status reads as
 * published here, never as draft. "Scheduled" is not a stored
 * status -- it is a page that goes live in the future (goLiveAt): a published
 * page whose published_at is still ahead, or a draft the daily publish queue
 * has dated with scheduled_for. That is exactly how backend page_stats.py
 * counts it. Deriving it in one
 * place keeps the list, the editor pill and the filter chips in agreement.
 *
 * Kept free of React and router imports so it can be unit-tested directly.
 */

export const STATES = ['draft', 'scheduled', 'published', 'archived'];

export const STATE_LABELS = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  published: 'Published',
  archived: 'Archived',
};

/* A published_at that does not parse is treated as "already live" rather than
   "scheduled": a bad timestamp must not hide a page from the Published view. */
const parseDate = (value) => {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/* The stored timestamp a page goes live at, or null. A published page goes
   live at its published_at; a draft only once scripts/authoring/publish_queue.py
   has planned it (scheduled_for), since a draft's published_at is just when it
   was written. Mirrors backend page_stats.go_live_value. */
export function goLiveAt(page) {
  const status = String((page && page.status) || 'published').toLowerCase();
  if (status === 'published') return (page && page.published_at) || null;
  if (status === 'draft') return (page && page.scheduled_for) || null;
  return null;
}

export function derivePageState(page, now = new Date()) {
  const status = String((page && page.status) || 'published').toLowerCase();
  if (status === 'archived') return 'archived';
  const goLive = parseDate(goLiveAt(page));
  const future = !!goLive && goLive.getTime() > now.getTime();
  if (status !== 'published') return status === 'draft' && future ? 'scheduled' : 'draft';
  return future ? 'scheduled' : 'published';
}

/* Filter by derived state and by a case-insensitive title/slug substring.
   `state: 'all'` (or missing) keeps every page; an empty query matches all. */
export function filterPages(pages, { state = 'all', query = '', now = new Date() } = {}) {
  const q = String(query || '').trim().toLowerCase();
  return (pages || []).filter((page) => {
    if (!page) return false;
    if (state && state !== 'all' && derivePageState(page, now) !== state) return false;
    if (!q) return true;
    const title = String(page.title || '').toLowerCase();
    const slug = String(page.slug || page.id || page._id || '').toLowerCase();
    return title.includes(q) || slug.includes(q);
  });
}

/* { draft: n, scheduled: n, published: n, archived: n, all: n } */
export function countByState(pages, now = new Date()) {
  const counts = { all: 0, draft: 0, scheduled: 0, published: 0, archived: 0 };
  (pages || []).forEach((page) => {
    if (!page) return;
    counts.all += 1;
    counts[derivePageState(page, now)] += 1;
  });
  return counts;
}

/* Background / foreground pair for a state pill. */
export function stateColor(state) {
  switch (state) {
    case 'published':
      return { bg: '#d4edda', fg: '#155724' };
    case 'scheduled':
      return { bg: '#cce5ff', fg: '#004085' };
    case 'archived':
      return { bg: '#e2e3e5', fg: '#383d41' };
    case 'draft':
    default:
      return { bg: '#fff3cd', fg: '#856404' };
  }
}

/* datetime-local <-> ISO. The input speaks local wall-clock time with no zone;
   the API speaks ISO/UTC. Empty stays empty (the save layer sends null). */
const pad = (n) => String(n).padStart(2, '0');

export function isoToLocalInput(iso) {
  const d = parseDate(iso);
  if (!d) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function localInputToIso(local) {
  if (!local) return null;
  const d = new Date(local); // no zone suffix => parsed as local time
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}
